import React from 'react';
import { motion } from 'motion/react';
import { useParams, Link } from 'react-router-dom';
import { 
  ArrowLeft, 
  Clock, 
  Twitter, 
  Share2, 
  BadgeCheck, 
  Calendar,
  ChevronRight,
  Rocket,
  CheckCircle2,
  Star,
  Info,
  BookOpen,
  HelpCircle,
  FileText,
  ArrowRight
} from 'lucide-react';
import SEO from '@/components/SEO';
import { Badge } from '@/components/ui/badge';
import { BackgroundAnimation } from '@/components/background-animation';
import { DiscordCTA } from '@/components/DiscordCTA';
import InternalLinksSection from '@/components/InternalLinksSection';
import { UPDATES } from '@/data/updates';

const renderTextWithLinks = (text: string) => {
  const parts = [];
  const regex = /\[([^\]]+)\]\(([^)]+)\)/g;
  let lastIndex = 0;
  let match;

  while ((match = regex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push(text.substring(lastIndex, match.index));
    }

    const linkText = match[1];
    const linkUrl = match[2];

    if (linkUrl.startsWith('mailto:') || linkUrl.startsWith('http://') || linkUrl.startsWith('https://')) {
      parts.push(
        <a 
          key={match.index} 
          href={linkUrl} 
          className="text-cyan-600 hover:text-cyan-800 underline decoration-cyan-600/30 hover:decoration-cyan-800 transition-colors cursor-pointer"
        >
          {linkText}
        </a>
      );
    } else {
      parts.push(
        <Link 
          key={match.index} 
          to={linkUrl} 
          className="text-cyan-600 hover:text-cyan-800 underline decoration-cyan-600/30 hover:decoration-cyan-800 transition-colors cursor-pointer"
        >
          {linkText}
        </Link>
      );
    }

    lastIndex = regex.lastIndex;
  }

  if (lastIndex < text.length) {
    parts.push(text.substring(lastIndex));
  }

  return parts.length > 0 ? parts : text;
};

const UpdateArticle: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const update = UPDATES.find(u => u.slug === slug);

  if (!update) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-zinc-50">
        <div className="text-center space-y-6">
          <h1 className="text-4xl font-bold font-display uppercase italic text-zinc-300">File Not Found</h1>
          <p className="text-zinc-500 uppercase tracking-widest text-[10px] font-bold">The requested update log is missing.</p>
          <Link to="/updates" className="inline-flex items-center gap-2 text-black font-black uppercase tracking-widest text-[10px] border border-black/10 px-6 py-3 rounded-xl hover:bg-black hover:text-white transition-all">
            <ArrowLeft className="w-4 h-4" /> Return to Logs
          </Link>
        </div>
      </div>
    );
  }

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'FEATURE': return <Rocket className="w-3 h-3" />;
      case 'FIX': return <CheckCircle2 className="w-3 h-3" />;
      case 'IMPROVEMENT': return <Star className="w-3 h-3" />;
      default: return <Info className="w-3 h-3" />;
    }
  };

  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'FEATURE': return 'bg-cyan-50 text-cyan-600 border-cyan-100';
      case 'FIX': return 'bg-emerald-50 text-emerald-600 border-emerald-100';
      case 'IMPROVEMENT': return 'bg-purple-50 text-purple-600 border-purple-100';
      default: return 'bg-zinc-50 text-zinc-600 border-zinc-100';
    }
  };

  return (
    <div className="min-h-screen bg-[#ffffff] text-black selection:bg-black selection:text-white font-sans pt-20 pb-24 overflow-x-hidden">
      <BackgroundAnimation opacity={0.1} />
      <SEO 
        title={update.title}
        description={update.description}
        keywords={`${update.category}, app updates, changelog, fun circle games`}
      />
      
      <div className="max-w-4xl mx-auto px-6 relative z-10">
        <motion.div 
           initial={{ opacity: 0, y: 10 }}
           animate={{ opacity: 1, y: 0 }}
           className="mb-12 space-y-8 mt-6"
        >
          <Link to="/updates" className="inline-flex items-center gap-2 text-xs font-bold text-zinc-400 hover:text-cyan-600 transition-all group uppercase tracking-widest bg-cyan-50/50 px-4 py-2 rounded-lg border border-cyan-100">
            <ArrowLeft className="w-3 h-3 group-hover:-translate-x-1 transition-transform" />
            System Logs
          </Link>

          <div className="space-y-6">
            <div className="flex items-center gap-4">
              <span className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-[10px] font-black tracking-[0.15em] border uppercase ${getCategoryColor(update.category)}`}>
                {getCategoryIcon(update.category)}
                {update.category}
              </span>
              {update.version && (
                <span className="px-3 py-1.5 rounded-lg text-[10px] font-bold text-cyan-400 border border-cyan-100 uppercase tracking-widest bg-cyan-50/30">
                  v{update.version}
                </span>
              )}
            </div>

            <h1 className="text-4xl md:text-5xl font-black text-cyan-950 tracking-tight leading-[1.1]">
              {update.title}
            </h1>
            
            <p className="text-xl text-zinc-500 font-medium leading-relaxed max-w-2xl">
              {update.description}
            </p>
          </div>

          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-6 py-8 border-y border-cyan-50 mt-12 bg-cyan-50/30 px-6 rounded-3xl">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-2xl bg-cyan-600 flex items-center justify-center text-[10px] font-bold text-white uppercase tracking-tighter shadow-lg shadow-cyan-600/20">
                DEV
              </div>
              <div className="flex flex-col">
                 <div className="flex items-center gap-2">
                    <span className="text-sm font-bold text-cyan-950 uppercase tracking-tight">{update.author.name}</span>
                  </div>
                 <span className="text-[10px] text-zinc-400 font-bold uppercase tracking-widest flex items-center gap-2">
                    <Calendar className="w-3 h-3" />
                    {update.date}
                 </span>
              </div>
            </div>
            <div className="flex items-center gap-3">
               <button 
                onClick={() => {
                  window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(update.title)}&url=${encodeURIComponent(window.location.href)}`, '_blank');
                }}
                className="w-10 h-10 rounded-xl border border-cyan-100 bg-white flex items-center justify-center hover:bg-cyan-600 hover:text-white transition-all shadow-sm"
               >
                  <Twitter className="w-4 h-4" />
               </button>
               <button 
                onClick={() => {
                  const url = window.location.href;
                  if (navigator.share) {
                    navigator.share({ title: update.title, url });
                  } else {
                    navigator.clipboard.writeText(url);
                    alert('Link copied to clipboard!');
                  }
                }}
                className="w-10 h-10 rounded-xl border border-cyan-100 bg-white flex items-center justify-center hover:bg-cyan-600 hover:text-white transition-all shadow-sm"
               >
                  <Share2 className="w-4 h-4" />
               </button>
            </div>
          </div>
        </motion.div>

        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="space-y-12"
        >
          {update.content.map((block, idx) => {
            switch (block.type) {
              case 'heading':
                return (
                  <h2 key={idx} className="text-2xl font-black tracking-tight mt-16 mb-6 text-cyan-950 border-l-4 border-cyan-600 pl-6">
                    {block.content}
                  </h2>
                );
              case 'text':
                return (
                  <p key={idx} className="text-zinc-600 text-lg leading-relaxed mb-6 font-medium">
                    {renderTextWithLinks(block.content as string)}
                  </p>
                );
              case 'highlight':
                return (
                  <div key={idx} className="bg-cyan-50/30 p-10 rounded-[2.5rem] border border-cyan-100 my-10 shadow-sm relative overflow-hidden group">
                    <div className="absolute top-0 right-0 w-32 h-32 bg-cyan-200/20 blur-3xl rounded-full -z-0 group-hover:scale-150 transition-transform duration-700" />
                    <p className="text-cyan-950 font-bold text-xl leading-relaxed relative z-10 italic">
                      "{renderTextWithLinks(block.content as string)}"
                    </p>
                  </div>
                );
              case 'list':
                return (
                  <div key={idx} className="grid grid-cols-1 gap-4 my-10">
                    {(block.content as string[]).map((item, i) => (
                      <div key={i} className="flex items-start gap-4 p-6 rounded-2xl bg-white border border-cyan-50 hover:border-cyan-200 transition-all group">
                        <div className="mt-1 w-2 h-2 rounded-full bg-cyan-600 group-hover:scale-150 transition-transform" />
                        <span className="text-zinc-600 font-medium leading-relaxed">{renderTextWithLinks(item)}</span>
                      </div>
                    ))}
                  </div>
                );
              case 'quote':
                return (
                  <blockquote key={idx} className="border-l-0 border-zinc-100 pl-0 italic text-2xl font-medium text-zinc-400 my-16 py-8 text-center max-w-2xl mx-auto">
                    <span className="text-zinc-100 text-6xl block h-4 font-serif leading-none opacity-50">"</span>
                    {renderTextWithLinks(block.content as string)}
                    <span className="text-zinc-100 text-6xl block h-4 font-serif leading-none opacity-50 mt-4 text-right">"</span>
                  </blockquote>
                );
              default:
                return null;
            }
          })}
        </motion.div>

        <div className="mt-24 pt-16 border-t border-cyan-50 bg-cyan-50/10 -mx-6 px-6 pb-24">
           <div className="max-max-4xl mx-auto">
             <div className="flex items-center justify-between mb-12">
                <h3 className="text-xl font-black text-cyan-950 tracking-tight uppercase">Recent Updates</h3>
                <Link to="/updates" className="text-xs font-bold text-cyan-500 hover:text-cyan-700 transition-all uppercase tracking-widest border-b border-transparent hover:border-cyan-700">Browse History</Link>
             </div>
             
             <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {UPDATES.filter(u => u.slug !== slug).slice(0, 2).map(recent => (
                   <Link key={recent.slug} to={`/updates/${recent.slug}`} className="group p-8 rounded-[2rem] bg-white border border-cyan-50 hover:border-cyan-600/20 hover:shadow-xl hover:shadow-cyan-900/5 transition-all space-y-4">
                      <div className="flex items-center gap-3">
                         <span className={`px-2.5 py-1 rounded-md text-[8px] font-black tracking-widest uppercase border ${getCategoryColor(recent.category)}`}>
                            {recent.category}
                         </span>
                         <span className="text-[9px] font-bold text-cyan-200 uppercase tracking-widest">{recent.date}</span>
                      </div>
                      <h4 className="text-lg font-bold text-cyan-950 group-hover:text-cyan-600 transition-colors tracking-tight leading-tight">
                         {recent.title}
                      </h4>
                      <ChevronRight className="w-4 h-4 text-cyan-200 group-hover:translate-x-1 transition-transform ml-auto" />
                   </Link>
                ))}
             </div>

             {/* Related Resources Grid */}
             <div className="mt-20 pt-16 border-t border-cyan-50 space-y-6">
               <h3 className="text-lg font-bold font-display uppercase tracking-tight text-cyan-950">Changelog & Learning Resources</h3>
               <p className="text-zinc-500 text-sm font-medium font-sans">
                 Want to learn how to use these new updates? Explore our developer & creator academy resources:
               </p>
               <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
                 <Link to="/tutorials" className="p-6 rounded-3xl bg-white border border-cyan-50 hover:border-cyan-600/20 transition-all flex flex-col justify-between group hover:shadow-lg hover:shadow-cyan-950/5">
                   <div className="space-y-4">
                     <div className="w-10 h-10 rounded-2xl bg-cyan-50 text-cyan-600 flex items-center justify-center group-hover:scale-110 transition-transform">
                       <BookOpen className="w-5 h-5" />
                     </div>
                     <h4 className="font-bold text-cyan-950 uppercase tracking-tight">Creator Academy</h4>
                     <p className="text-xs text-zinc-500 font-medium">Read tutorials detailing how to align physics parameters to beats.</p>
                   </div>
                   <div className="mt-6 text-[10px] font-black uppercase tracking-widest text-cyan-600 flex items-center gap-1">
                     Tutorials <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform" />
                   </div>
                 </Link>

                 <Link to="/help" className="p-6 rounded-3xl bg-white border border-cyan-50 hover:border-cyan-600/20 transition-all flex flex-col justify-between group hover:shadow-lg hover:shadow-cyan-950/5">
                   <div className="space-y-4">
                     <div className="w-10 h-10 rounded-2xl bg-cyan-50 text-cyan-600 flex items-center justify-center group-hover:scale-110 transition-transform">
                       <HelpCircle className="w-5 h-5" />
                     </div>
                     <h4 className="font-bold text-cyan-950 uppercase tracking-tight">Help Documentation</h4>
                     <p className="text-xs text-zinc-500 font-medium">100+ feature guides for all sliders and bounciness settings.</p>
                   </div>
                   <div className="mt-6 text-[10px] font-black uppercase tracking-widest text-cyan-600 flex items-center gap-1">
                     Read Guides <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform" />
                   </div>
                 </Link>

                 <Link to="/blog" className="p-6 rounded-3xl bg-white border border-cyan-50 hover:border-cyan-600/20 transition-all flex flex-col justify-between group hover:shadow-lg hover:shadow-cyan-950/5">
                   <div className="space-y-4">
                     <div className="w-10 h-10 rounded-2xl bg-cyan-50 text-cyan-600 flex items-center justify-center group-hover:scale-110 transition-transform">
                       <FileText className="w-5 h-5" />
                     </div>
                     <h4 className="font-bold text-cyan-950 uppercase tracking-tight">System Blog</h4>
                     <p className="text-xs text-zinc-500 font-medium">Read articles regarding creator trends, ASMR psychology & virality.</p>
                   </div>
                   <div className="mt-6 text-[10px] font-black uppercase tracking-widest text-cyan-600 flex items-center gap-1">
                     Read Blog <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform" />
                   </div>
                 </Link>
               </div>
             </div>

             <div className="mt-12">
               <DiscordCTA variant="banner" />
             </div>

             <div className="mt-12">
               <InternalLinksSection />
             </div>
           </div>
        </div>
      </div>
    </div>
  );
};

export default UpdateArticle;
